import { useState } from "react";
import { Sparkles, Loader2, X, ClipboardList } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import { useAgent } from "./AgentProvider";
import AgentMarkdown from "./AgentMarkdown";

type Mode = "brief" | "handover";

const MODES: Record<
  Mode,
  { endpoint: string; label: string; heading: string; Icon: typeof Sparkles }
> = {
  brief: {
    endpoint: "/api/admin/agent/brief",
    label: "Brief me",
    heading: "AI brief",
    Icon: Sparkles,
  },
  handover: {
    endpoint: "/api/admin/agent/handover",
    label: "Handover note",
    heading: "Handover note",
    Icon: ClipboardList,
  },
};

/**
 * Single-shot grounded order summary (Block 3 B3.1/B3.3). `mode="brief"` → POST /agent/brief
 * (operator's own quick brief); `mode="handover"` → POST /agent/handover (velín summary for a
 * colleague covering the order, vision §5.16). Server pre-fetches the order context (one Haiku
 * completion, no tool loop); result shown in a dismissible popover. Hidden until the key is set.
 */
export default function BriefButton({ orderId, mode = "brief" }: { orderId: string; mode?: Mode }) {
  const { enabled } = useAgent();
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enabled) return null;

  const cfg = MODES[mode];
  const { Icon } = cfg;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ApiResponse<{ text: string }>>(cfg.endpoint, {
        entityType: "order",
        entityId: orderId,
      });
      setText(res.data.text);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Couldn't generate the ${mode}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={run}
        disabled={busy}
        className="border-primary-200 text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
        {cfg.label}
      </button>

      {(text || error) && (
        <div className="border-primary-100 bg-primary-50 dark:border-primary-900/40 absolute right-0 top-full z-30 mt-2 w-[26rem] max-w-[90vw] rounded-xl border p-4 shadow-xl dark:bg-gray-800">
          <button
            onClick={() => {
              setText(null);
              setError(null);
            }}
            aria-label="Dismiss"
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="text-primary-700 dark:text-primary-300 mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <Icon className="h-3.5 w-3.5" /> {cfg.heading}
          </div>
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <AgentMarkdown>{text!}</AgentMarkdown>
          )}
        </div>
      )}
    </div>
  );
}
